import { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  Card,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { GeneralPractitionerGrid } from 'src/careflow/helpers/types';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import {
  PractitionerWrapper,
  WrappedPractitioner,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Practitioner';
import {
  Bundle,
  Coding,
  FhirResource,
  Organization,
  Practitioner,
  PractitionerRole,
  Reference,
} from 'src/careflow/ph-core/fhir-client/resources';
import { usePractitionerRoles, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';

import { debounce } from 'src/utils/timers';
import { cleanSearchInput } from 'src/utils/string';
import { isActiveCondition } from 'src/careflow/helpers/constants';
import { fhirClient } from 'src/App';
import { getReferenceId, getResourcesAndRelatedResources } from 'src/utils/fhir';
import useNotiMessage from 'src/hooks/useNotiMessage';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import { CustomLoadingButton } from 'src/careflow/common/CustomButton';
import { Add } from '@mui/icons-material';
import CancelButton from 'src/careflow/common/CancelButton';
import { PractitionerRoleWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/PractitionerRole';
import { isEmpty } from 'lodash';

export interface CreatePractitionerRoleDialog {
  isVisible: boolean;
  onDialogClose: () => void;
  selectedResource: WrappedPractitioner | WrappedOrganization;
  onPractitionerRoleCreation?: Function;
}

export interface Resource {
  id: string;
  name: string;
  resourceType: string;
}

export interface ResourceQueryParams {
  search?: string;
  resourceType: 'Practitioner' | 'Organization';
}

const CreatePractitionerRoleDialog = ({
  isVisible,
  onDialogClose,
  selectedResource,
  onPractitionerRoleCreation,
}: CreatePractitionerRoleDialog) => {
  const [roleValue, setRoleValue] = useState<Coding | null>(null);
  const [isLoadingResources, setIsLoadingResources] = useState(true);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedAdditionalResource, setSelectedAdditionalResource] = useState<Resource | null>(
    null
  );
  const [resourceSearch, setResourceSearch] = useState('');
  const [existingPractitionerRoles, setExistingPractitionerRoles] = useState<
    GeneralPractitionerGrid[]
  >([]);
  const [isPractitionerRolesLoading, setIsPractitionerRolesLoading] = useState(false);
  const [isCreatingPractitionerRole, setIsCreatingPractitionerRole] = useState(false);

  const [rolesCodesVs] = useValueSet('ph-roles-codes', {
    map: ValueSetWrapper,
  });

  const [_, { create: createPractitionerRole }] = usePractitionerRoles({
    map: PractitionerRoleWrapper,
    autofetch: false,
  });

  const { showErrorMessage, showSuccessMessage } = useNotiMessage();

  const translate = useTranslations(`practitioner`);

  const debouncedGetResources = useCallback(
    debounce(async (queryParams: ResourceQueryParams) => {
      setResources([]);
      setIsLoadingResources(true);
      const resources = await getResources(queryParams);
      setResources(resources.filter(({ name }) => name.trim()));
      setIsLoadingResources(false);
    }, 500),
    []
  );

  useEffect(() => {
    if (!selectedResource?.resourceType || !isVisible) return;
    debouncedGetResources({
      resourceType:
        selectedResource.resourceType === 'Practitioner' ? 'Organization' : 'Practitioner',
      search: resourceSearch,
    });
  }, [resourceSearch, selectedResource, isVisible]);

  useEffect(() => {
    if (isVisible) return;
    setSelectedAdditionalResource(null);
    setRoleValue(null);
    setResourceSearch('');
    setExistingPractitionerRoles([]);
  }, [isVisible]);

  const getExistingPractitionerRoles = useCallback(async () => {
    const isPractitioner = selectedResource?.resourceType === 'Practitioner';
    setExistingPractitionerRoles([]);
    if (!roleValue?.code) {
      return;
    }
    setIsPractitionerRolesLoading(true);

    const practitionerId = isPractitioner ? selectedResource.id : selectedAdditionalResource?.id;
    const organizationId = !isPractitioner ? selectedResource?.id : selectedAdditionalResource?.id;

    const filters = {
      role: roleValue.code,
      ...(practitionerId ? { practitioner: practitionerId } : { 'practitioner:missing': 'true' }),
      ...(organizationId ? { organization: organizationId } : { 'organization:missing': 'true' }),
      active: 'true',
    };

    const urlSearchParams = new URLSearchParams(filters).toString();

    const prResponse = await fhirClient.search<Bundle>(
      `PractitionerRole?_include=PractitionerRole:practitioner&_include=PractitionerRole:organization&${urlSearchParams}`
    );

    const resources =
      prResponse?.entry?.reduce?.<FhirResource[]>((resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource];
      }, []) ?? [];

    const [practitionerRoles, relatedResources] = getResourcesAndRelatedResources(
      resources,
      'PractitionerRole'
    );

    const existingPRs: GeneralPractitionerGrid[] = (practitionerRoles as PractitionerRole[]).map(
      ({ id, code, practitioner: practitionerReference, organization: organizationReference }) => {
        const practitioner = relatedResources.find(
          ({ id }) => id === getReferenceId(practitionerReference?.reference)
        );
        const practitionerWrapped = PractitionerWrapper((practitioner ?? {}) as Practitioner);
        const organization = relatedResources.find(
          ({ id }) => id === getReferenceId(organizationReference?.reference)
        ) as Organization;
        return {
          id: id ?? '',
          role: code?.[0],
          practitioner: practitioner
            ? { id: practitioner.id ?? '', fullName: practitionerWrapped.getFullName() }
            : {
                id: getReferenceId(practitionerReference?.reference) ?? '',
                fullName: practitionerReference?.display,
              },
          organization: organization
            ? { id: organization.id ?? '', fullName: organization?.name }
            : {
                id: getReferenceId(organizationReference?.reference) ?? '',
                fullName: organizationReference?.display,
              },
        };
      }
    );

    setExistingPractitionerRoles(existingPRs);
    setIsPractitionerRolesLoading(false);
  }, [roleValue, selectedAdditionalResource, selectedResource]);

  useEffect(() => {
    getExistingPractitionerRoles();
  }, [getExistingPractitionerRoles]);

  const handlePractitionerRoleCreation = async () => {
    try {
      if (!roleValue) return null;
      setIsCreatingPractitionerRole(true);
      const practitioner: Reference | undefined = isPractitioner
        ? {
            reference: `Practitioner/${selectedResource.id}`,
            display: selectedResource.getFullName() ?? undefined,
          }
        : selectedAdditionalResource?.id
        ? {
            reference: `Practitioner/${selectedAdditionalResource.id}`,
            display: selectedAdditionalResource.name,
          }
        : undefined;

      const organization: Reference | undefined = !isPractitioner
        ? {
            reference: `Organization/${selectedResource?.id}`,
            display: selectedResource?.name,
          }
        : selectedAdditionalResource?.id
        ? {
            reference: `Organization/${selectedAdditionalResource.id}`,
            display: selectedAdditionalResource.name,
          }
        : undefined;

      const practitionerRolePayload: PractitionerRole = {
        active: true,
        code: [
          {
            coding: [
              {
                ...roleValue,
              },
            ],
          },
        ],
        resourceType: 'PractitionerRole',
        practitioner: practitioner ?? undefined,
        organization: organization ?? undefined,
      };

      const practitionerRole = await createPractitionerRole(practitionerRolePayload);

      setIsCreatingPractitionerRole(false);
      showSuccessMessage(translate('successMessage.createPractitioneRole'));
      onPractitionerRoleCreation?.(practitionerRole?.[0]);
      onDialogClose();
      if (!practitionerRole) {
        throw new Error('Unable to create practitioner role');
      }
    } catch (error) {
      showErrorMessage(translate('errorMessage.createPractitioneRole'));
    }
  };

  const isPractitioner = selectedResource?.resourceType === 'Practitioner';
  const resource = {
    fullName: isPractitioner ? selectedResource.getFullName() : selectedResource.name,
    npi: selectedResource.getNPI(),
  };

  return (
    <Dialog open={isVisible} onClose={onDialogClose} fullWidth maxWidth="sm" keepMounted={false}>
      <DialogTitle id="confirm-dialog-title">{translate('createPractitionerRole')}</DialogTitle>
      <Card>
        <DialogContent sx={{ paddingTop: 2 }}>
          <>
            <Box sx={{ marginBottom: 1 }}>
              <Typography sx={{ fontSize: '0.85rem', marginBottom: 1 }}>
                You selected:{' '}
                <span style={{ fontWeight: 'bold' }}>
                  {isPractitioner ? 'Practitioner' : 'Organization'}
                </span>
              </Typography>

              <Typography sx={{ fontWeight: 'bold', fontSize: '0.90rem' }}>
                {resource?.fullName}
              </Typography>
              <Typography sx={{ fontSize: '0.90rem' }}>
                <span style={{ fontWeight: 'bold' }}>NPI: </span> {resource?.npi}
              </Typography>
            </Box>
            {!isEmpty(translate('bySelectingRole')) && (
              <Box>
                <Typography sx={{ fontSize: '0.85rem', marginBottom: 2 }}>
                  {translate('bySelectingRole')}{' '}
                  {isPractitioner
                    ? `${translate('anOrganization')}`
                    : `${translate('aPractitioner')}`}
                  , {translate('couldCreatePractitionerRole')}
                </Typography>
              </Box>
            )}
            
            <Grid container justifyContent={'space-between'} alignItems={'center'}>
              <Grid item xs={5.8}>
                <Autocomplete
                  fullWidth
                  disabled={!rolesCodesVs?.asListAll?.()?.length}
                  value={roleValue}
                  onChange={(_: React.SyntheticEvent, role) => {
                    setRoleValue(role);
                  }}
                  options={rolesCodesVs?.asListAll?.() ?? []}
                  getOptionLabel={(role: Coding) => role?.display as string}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={'Role'}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!rolesCodesVs?.asListAll?.()?.length ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={5.8}>
                <Autocomplete
                  value={selectedAdditionalResource}
                  fullWidth
                  onChange={(_: React.SyntheticEvent, resource) => {
                    setSelectedAdditionalResource(resource);
                  }}
                  onBlur={() => {
                    setResourceSearch('');
                  }}
                  options={resources ?? []}
                  getOptionLabel={({ name }: Resource) => name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      value={resourceSearch}
                      label={isPractitioner ? 'Organization' : 'Practitioner'}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const text = event.target.value;
                        setResourceSearch(text);
                      }}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingResources ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Typography
              sx={{
                fontSize: '0.80rem',
                marginBottom: 2,
                marginTop: 3,
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              {translate('ifPractitionerRoleExists')}
            </Typography>
            <TableContainer sx={{ mt: 2, maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('practitionerRoleHeading')}</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Organization</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isPractitionerRolesLoading ? (
                    existingPractitionerRoles.map(({ practitioner, organization, role, id }) => (
                      <TableRow key={id}>
                        <TableCell>
                          {isPractitioner
                            ? selectedResource.getFullName() ?? ''
                            : practitioner?.fullName ?? ''}
                        </TableCell>
                        <TableCell>
                          {roleValue?.display
                            ? roleValue?.display
                            : role?.coding?.[0]?.display ?? ''}
                        </TableCell>
                        <TableCell>
                          {!isPractitioner ? selectedResource?.name : organization?.fullName ?? ''}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={35} sx={{ marginTop: 2 }} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        </DialogContent>

        <DialogActions sx={{ flexDirection: 'row', justifyContent: 'center' }}>
          <Tooltip title={translate('createPractitioner')}>
            <span>
              <CustomLoadingButton
                loading={isCreatingPractitionerRole}
                startIcon={<Add />}
                disabled={
                  !!(
                    isPractitionerRolesLoading ||
                    existingPractitionerRoles?.length ||
                    isCreatingPractitionerRole ||
                    !roleValue?.code
                  )
                }
                onClick={handlePractitionerRoleCreation}
                variant="contained"
                sx={{ alignSelf: 'center' }}
              >
                Create
              </CustomLoadingButton>
            </span>
          </Tooltip>
          <CancelButton disabled={isCreatingPractitionerRole} onClick={onDialogClose} autoFocus />
        </DialogActions>
      </Card>
    </Dialog>
  );
};

const getResources = async (query: ResourceQueryParams): Promise<Resource[]> => {
  const { resourceType, search } = query;
  const cleansedSearch = cleanSearchInput(search ?? '');
  const splittedSearch = cleansedSearch ? cleansedSearch.split(' ') : [];
  const givenName = splittedSearch?.[0] ?? '';
  const familyName = splittedSearch.slice(1).join(' ');
  const practitionerFilters = cleansedSearch
    ? splittedSearch.length === 1
      ? {
          name: givenName,
        }
      : {
          given: givenName,
          ...(familyName ? { family: familyName } : {}),
        }
    : {};

  const organizationFilters = cleansedSearch ? { name: cleansedSearch } : {};

  const urlSearchParams = new URLSearchParams({
    ...(resourceType === 'Practitioner'
      ? (practitionerFilters as Record<string, string>)
      : (organizationFilters as Record<string, string>)),
    ...isActiveCondition,
  }).toString();

  const resourcesResponse = await fhirClient.search(`${resourceType}?${urlSearchParams}`);

  const resourceEntries =
    resourcesResponse?.entry?.reduce?.<(Practitioner | Organization)[]>(
      (resources, { resource }) => {
        if (!resource) return resources;
        return [...resources, resource as Practitioner | Organization];
      },
      []
    ) ?? [];

  return resourceEntries.map((resource) => {
    let resourceName = resource?.name;
    if (typeof resourceName === 'object') {
      const firstName = resourceName?.[0]?.given?.join?.(' ') ?? '';
      const lastName = resourceName?.[0]?.family ?? '';
      resourceName = `${firstName} ${lastName}`.trim();
    }
    return {
      id: resource.id as string,
      resourceType: resource.resourceType,
      name: resourceName ?? '',
    };
  });
};

export default CreatePractitionerRoleDialog;
